import { type TriggerProps } from '@atlaskit/popup';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import {
  useCommentActionsContext,
  useCommentStateContext,
} from '@databases/comments/hooks/useCommentContext';
import SelectValueMenu from '@databases/context-menu/select-value-menu';
import { createContextualMenuContext } from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-context';
import {
  createContextualMenuItems,
  type MenuItemMap,
} from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-items';
import { useContextualMenu } from '@databases/contextual-menu';
import { useDialog } from '@databases/dialog';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectValueRange,
  selectValueSingle,
  setFirstSelectedValue,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectIsValueSelected,
  selectSelectedValuesCount,
} from '@databases/redux/cellSelections/selectors';
import { blurActiveElement } from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { getValueId } from '@databases/utils/utils';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import TableLayoutCell, {
  type TableLayoutCellProps,
} from '../table-layout/TableLayoutCell';

type TableLayoutCellWithMenuProps = TableLayoutCellProps;

const TableLayoutCellWithMenu = React.memo(function TableLayoutCellWithMenu({
  contentId,
  entryId,
  fieldId,
  rowIndex,
  colIndex,
  isContentEditable,
  canEditContent,
  canEditFields,
  isSelectionDisabled,
  currentViewId,
  ...tableLayoutCellProps
}: TableLayoutCellWithMenuProps) {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const { setOpenThread } = useCommentActionsContext();
  const { openThread } = useCommentStateContext();
  const { formatMessage } = useIntl();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { toggleOpenMenu, closeMenu } = useContextualMenu({
    type: 'value',
    entryId,
    fieldId,
  });

  const onIsSelectedChange = useCallback(
    (options?: { isMultiSelectOn?: boolean }) => {
      const startedRenderingAt = performance.now();
      closeMenu();

      dispatch(setSoftSelection({ value: false }));
      if (
        selectSelectedValuesCount(store.getState(), {
          contentId,
        }) === 0
      ) {
        dispatch(setFirstSelectedValue({ value: { entryId, fieldId } }));
      }
      if (options?.isMultiSelectOn) {
        dispatch(
          selectValueRange({
            contentId,
            entryId: entryId,
            fieldId: fieldId,
          })
        );
      } else {
        dispatch(
          selectValueSingle({
            contentId,
            entryId: entryId,
            fieldId: fieldId,
          })
        );
        dispatch(setSoftSelection({ value: true }));
      }

      const reliability = ReliabilityMetrics.getInstance();
      reliability.successWithDuration(
        MetricName.DATABASE_SELECTION_CHANGE,
        startedRenderingAt,
        performance.now()
      );
    },
    [closeMenu, store, contentId, dispatch, entryId, fieldId]
  );

  const handleSelectValue = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      const multipleValuesSelected =
        selectSelectedValuesCount(store.getState(), {
          contentId,
        }) > 1;
      const isMultiSelectOn = !!e && isModifierKeyPressed(e, ['shiftKey']);
      if (!multipleValuesSelected) {
        onIsSelectedChange({ isMultiSelectOn });
      }
    },
    [contentId, onIsSelectedChange, store]
  );

  const isContextMenuEnabled = isPluginEnabled(PluginKey.CONTEXT_MENU);

  const [menuItems, setMenuItems] = useState<MenuItemMap>({});
  const [canInsertContent, setCanInsertContent] = useState(false);
  const dialogs = useDialog();
  const onCloseMenu = () => {
    setMenuItems({});
  };

  const actionBar = useSnapshot(actionBarState.state);

  const handleOnContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (actionBar.isActionBarEnabled) {
        return;
      }

      handleSelectValue(e);

      // Show native context menu on text edit
      const target = e.target as HTMLElement;
      const isEditableTextArea = target.tagName.toLocaleLowerCase() === 'textarea';
      if (isEditableTextArea) {
        return;
      }

      const context = createContextualMenuContext({
        canEditContent,
        canEditFields,
        comments: {
          setOpenThread,
        },
        currentViewId,
        contentId,
        dialogs,
        entryId,
        fieldId,
        formatMessage,
        isDBAiFirstCreationEnabled,
      });
      const computedMenuItems = createContextualMenuItems(context);

      setMenuItems(computedMenuItems);
      setCanInsertContent(context.capabilities.canInsertContent);

      const isSelected = selectIsValueSelected(store.getState(), {
        entryId,
        fieldId,
      });

      if (openThread?.valueId === getValueId(entryId, fieldId)) {
        return;
      }

      if (isSelected && isContentEditable) {
        e.preventDefault();
        toggleOpenMenu();
        blurActiveElement();
      }
    },
    [
      handleSelectValue,
      store,
      canEditContent,
      canEditFields,
      contentId,
      currentViewId,
      dialogs,
      entryId,
      fieldId,
      formatMessage,
      isDBAiFirstCreationEnabled,
      isContentEditable,
      openThread?.valueId,
      toggleOpenMenu,
      setOpenThread,
      actionBar.isActionBarEnabled,
    ]
  );

  const triggerComponent = useCallback(
    (triggerProps?: TriggerProps) => (
      <TableLayoutCell
        contentId={contentId}
        entryId={entryId}
        fieldId={fieldId}
        rowIndex={rowIndex}
        colIndex={colIndex}
        isContentEditable={isContentEditable}
        canEditContent={canEditContent}
        canEditFields={canEditFields}
        currentViewId={currentViewId}
        isSelectionDisabled={isSelectionDisabled}
        onIsSelectedChange={onIsSelectedChange}
        handleOnContextMenu={isContextMenuEnabled ? handleOnContextMenu : undefined}
        {...tableLayoutCellProps}
        {...triggerProps}
        isFirstInRow={tableLayoutCellProps.isFirstInRow}
        ref={triggerProps?.ref}
      />
    ),
    [
      canEditContent,
      canEditFields,
      colIndex,
      contentId,
      currentViewId,
      entryId,
      fieldId,
      handleOnContextMenu,
      isContentEditable,
      isSelectionDisabled,
      onIsSelectedChange,
      rowIndex,
      tableLayoutCellProps,
      isContextMenuEnabled,
    ]
  );

  return (
    <SelectValueMenu
      Trigger={triggerComponent}
      contentId={contentId}
      currentViewId={currentViewId}
      canEditContent={canEditContent}
      entryId={entryId}
      fieldId={fieldId}
      menuItems={menuItems}
      canInsertContent={canInsertContent}
      onCloseMenu={onCloseMenu}
    />
  );
});

export default TableLayoutCellWithMenu;
